import { FC, useContext, useState } from "react";
import type { FieldErrors } from "react-hook-form";

import { Body } from "capitalrx-components-2.0/lib/typography";
import { Buttons, Modal, useAlert } from "capitalrx-components-2.0/lib/general";
import { useForm } from "capitalrx-components-2.0/lib/forms";

import { updateProviderManagementAPI } from "@/api";
import { StatusFormBlock } from "@/constants";
import { ProviderManagement } from "@/api/interfaces";
import { deepEqual } from "@/utils";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { AuthenticationContext } from "@/context";

import { ContactInformationForm } from "./ContactInformationForm";
import { ProviderContactInformationDataForm } from "./ContactInformationGroupFields";
import {
  contactInformationErrorMessage,
  getBannerMessages,
  getFieldBannerMessage,
  getServerFormField,
} from "./helper";
import type { ContactInformationField } from "./helper";

interface ContactInformationProps {
  readonly provider: ProviderManagement.ProviderSchema;
  readonly setProvider: Function;
  readonly onComplete: () => void;
  readonly type: StatusFormBlock;
  readonly onCancel: () => void;
  readonly onEdit: () => void;
}

export const ContactInformation: FC<ContactInformationProps> = ({ provider, setProvider, onComplete, onCancel, type, onEdit }) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();

  const defaultValues: ProviderContactInformationDataForm = {
    official: {
      title: provider.authorized_official_title,
      first_name: provider.authorized_official_first_name,
      last_name: provider.authorized_official_last_name,
      email_address: provider.authorized_official_email,
      fax_number: provider.authorized_official_fax,
      phone_number: provider.authorized_official_phone,
    },
    primary: {
      title: provider.primary_contact_title,
      first_name: provider.primary_contact_first_name,
      last_name: provider.primary_contact_last_name,
      email_address: provider.primary_contact_email,
      phone_number: provider.primary_contact_phone,
    },
    secondary: {
      title: provider.secondary_contact_title,
      first_name: provider.secondary_contact_first_name,
      last_name: provider.secondary_contact_last_name,
      email_address: provider.secondary_contact_email,
      phone_number: provider.secondary_contact_phone,
    },
  };

  const form = useForm<ProviderContactInformationDataForm>({
    defaultValues,
  });

  const showFieldErrorBanner = (messages: string[]): void => {
    if (messages.length === 0) {
      return;
    }

    showAlert({
      type: "error",
      style: "banner",
      message: messages.join("; "),
    });
  };

  const handleInvalidSubmit = (errors: FieldErrors<ProviderContactInformationDataForm>): void => {
    showFieldErrorBanner(
      getBannerMessages(errors as Record<string, unknown>),
    );
  };

  const submitHandler = async (data: ProviderContactInformationDataForm): Promise<void> => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update);
    try {
      const payload = {
        authorized_official_title: data.official.title,
        authorized_official_first_name: data.official.first_name,
        authorized_official_last_name: data.official.last_name,
        authorized_official_fax: data.official.fax_number,
        authorized_official_phone: data.official.phone_number,
        primary_contact_title: data.primary.title,
        primary_contact_first_name: data.primary.first_name,
        primary_contact_last_name: data.primary.last_name,
        primary_contact_email: data.primary.email_address,
        primary_contact_phone: data.primary.phone_number,
        secondary_contact_title: data.secondary.title,
        secondary_contact_first_name: data.secondary.first_name,
        secondary_contact_last_name: data.secondary.last_name,
        secondary_contact_email: data.secondary.email_address,
        secondary_contact_phone: data.secondary.phone_number,
      };
      await updateProviderManagementAPI(payload, getAuthToken());
      setProvider({ ...provider, ...payload });
      onComplete();
      showAlert({
        type: "success",
        style: "banner",
        message: "Successfully submitted contact information",
      });
    } catch (error) {
      const err = error as {
        response?: {
          data?: {
            message?: string;
            fields_with_errors?: Record<string, string | string[]>;
          };
        };
      };

      const serverFieldErrors = err.response?.data?.fields_with_errors;

      if (serverFieldErrors && typeof serverFieldErrors === "object") {
        const bannerMessages = new Set<string>();
        let firstInvalidField: ContactInformationField | undefined;

        Object.entries(serverFieldErrors).forEach(([field, rawMessage]) => {
          const formField = getServerFormField(field);
          const message = Array.isArray(rawMessage)
            ? rawMessage.join(", ")
            : rawMessage;

          if (!message) {
            return;
          }

          if (formField) {
            if (!firstInvalidField) {
              firstInvalidField = formField;
            }

            form.setError(formField, {
              type: "server",
              message,
            });

            bannerMessages.add(getFieldBannerMessage(formField, message));
            return;
          }

          bannerMessages.add(message);
        });

        if (firstInvalidField) {
          form.setFocus(firstInvalidField);
        }

        const resolvedMessages = Array.from(bannerMessages);
        if (resolvedMessages.length > 0) {
          showFieldErrorBanner(resolvedMessages);
        } else {
          showAlert({
            type: "error",
            style: "banner",
            message: err.response?.data?.message || contactInformationErrorMessage,
          });
        }

        console.error(error);
        logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
        return;
      }

      console.error(error);
      showAlert({
        type: "error",
        style: "banner",
        message: err.response?.data?.message || contactInformationErrorMessage,
      });
      // analytics log validate fail
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
      return;
    }

    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update_success);
  };

  const [discardChangeModal, setDiscardChangeModal] = useState(false);
  const discardChangeModalHandler = (approved: boolean) => {
    setDiscardChangeModal(false);
    if (approved) {
      onCancel();
      form.reset(defaultValues);
    }
  };
  const cancelHandler = () => {
    if (!deepEqual(provider, form.getValues())) {
      setDiscardChangeModal(true);
    } else {
      onCancel();
    }
  };
  return (
    <>
      <ContactInformationForm
        onCancel={cancelHandler}
        onSubmit={submitHandler}
        onInvalid={handleInvalidSubmit}
        type={type}
        form={form}
        onEdit={onEdit}
      />
      <Modal.Approve
        title="Discard Changes"
        onClose={discardChangeModalHandler}
        visible={discardChangeModal}
        approveButtonAppearance={Buttons.Destructive.Primary.Large}
        denyContent="Cancel"
        approveContent="Discard"
        testId="edit_contact_information_discard"
      >
        <Body>Unsaved changes will be lost if you proceed</Body>
      </Modal.Approve>
    </>
  );
};
